import pick from 'lodash/pick';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import {
  initiatePrivileged,
  markTxUnread,
  markTxRead,
  transitionPrivileged,
  completePayoutTransaction,
  vatPreview as vatPreviewApi,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { trackIntercomEvent } from '../../util/intercom';
import {
  TRANSITION_REQUEST_PAYMENT,
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_CONFIRM_PROJECT,
  TRANSITION_CANCEL_PAYMENT,
  isPrivileged,
  TRANSITION_ACCEPT_OFFER,
} from '../../util/transaction';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { withGateCode } from '../../util/vatGate';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';
export const CLEAR_INITIATE_ORDER_ERROR = 'app/CheckoutPage/CLEAR_INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const CANCEL_PAYMENT_REQUEST = 'app/CheckoutPage/CANCEL_PAYMENT_REQUEST';
export const CANCEL_PAYMENT_SUCCESS = 'app/CheckoutPage/CANCEL_PAYMENT_SUCCESS';
export const CANCEL_PAYMENT_ERROR = 'app/CheckoutPage/CANCEL_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/ListingPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/ListingPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/ListingPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const FETCH_VAT_PREVIEW_REQUEST = 'app/CheckoutPage/FETCH_VAT_PREVIEW_REQUEST';
export const FETCH_VAT_PREVIEW_SUCCESS = 'app/CheckoutPage/FETCH_VAT_PREVIEW_SUCCESS';
export const FETCH_VAT_PREVIEW_ERROR = 'app/CheckoutPage/FETCH_VAT_PREVIEW_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  bookingData: null,
  bookingDates: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  cancelPaymentError: null,
  stripeCustomerFetched: false,
  projectTransaction: null,
  vatPreview: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };
    case CLEAR_INITIATE_ORDER_ERROR:
      return { ...state, initiateOrderError: null };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case CANCEL_PAYMENT_REQUEST:
      return { ...state, cancelPaymentError: null };
    case CANCEL_PAYMENT_SUCCESS:
      return state;
    case CANCEL_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, cancelPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    // Clear the old preview: the next one may be for billing that was just saved,
    // and VIES can take 11-25 s. The panel stays hidden until the new preview is ok.
    case FETCH_VAT_PREVIEW_REQUEST:
      return { ...state, vatPreview: null };
    case FETCH_VAT_PREVIEW_SUCCESS:
      return { ...state, vatPreview: payload };
    case FETCH_VAT_PREVIEW_ERROR:
      return { ...state, vatPreview: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

export const clearInitiateOrderError = () => ({ type: CLEAR_INITIATE_ORDER_ERROR });

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

const cancelPaymentRequest = () => ({ type: CANCEL_PAYMENT_REQUEST });

const cancelPaymentSuccess = orderId => ({
  type: CANCEL_PAYMENT_SUCCESS,
  payload: orderId,
});

const cancelPaymentError = e => ({
  type: CANCEL_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (orderParams, transactionId) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;
  const transition = isTransition
    ? TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY
    : TRANSITION_REQUEST_PAYMENT;
  const isPrivilegedTransition = isPrivileged(transition);

  const bookingData = {
    startDate: orderParams.bookingStart,
    endDate: orderParams.bookingEnd,
    pricingPackage: orderParams.pricingPackage,
    suggestedPrice: orderParams.suggestedPrice,
    isCustomOffer: orderParams.isCustomOffer,
    projectBidAmount: orderParams.projectBidAmount,
    projectBidDescription: orderParams.projectBidDescription,
    projectTransactionId: orderParams.projectTransactionId,
    lePayoutTransactionId: orderParams.lePayoutTransactionId,
    projectBidDeliveryDate: orderParams.projectBidDeliveryDate,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition,
        params: orderParams,
      }
    : {
        processAlias: orderParams.isPayout
          ? config.lightPayoutProcessAlias
          : orderParams.isLightEntrepreneur
          ? config.lightProcessAlias
          : config.bookingProcessAlias,
        transition,
        params: orderParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      bookingStart: orderParams.bookingStart,
      bookingEnd: orderParams.bookingEnd,
    });
    return withGateCode(e).then(err => {
      dispatch(initiateOrderError(err));
      throw e;
    });
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged(
      { isSpeculative: false, bookingData, bodyParams, queryParams, include: ['provider'] },
      { expand: true }
    )
      .then(handleSuccess)
      .then(order => {
        const transaction = order;
        const transactionId = transaction.id;
        const provider = transaction.provider;

        // Send the message to the created transaction
        return markTxUnread({
          transactionId: transactionId.uuid,
          recipientId: provider.id.uuid,
        }).then(() => {
          return order;
        });
      })
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged(
      { isSpeculative: false, bookingData, bodyParams, queryParams, include: ['provider'] },
      { expand: true }
    )
      .then(handleSuccess)
      .then(order => {
        const transaction = order;
        const transactionId = transaction.id;
        const provider = transaction.provider;

        // Send the message to the created transaction
        return markTxUnread({
          transactionId: transactionId.uuid,
          recipientId: provider.id.uuid,
        }).then(() => {
          return order;
        });
      })
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// The defined keys of a protectedData object, forwarded as-is. Used so
// confirmPayment doesn't hand-list every VAT/booking key it passes through.
const definedKeys = obj =>
  Object.keys(obj || {})
    .filter(k => obj[k] !== undefined)
    .reduce((acc, k) => ({ ...acc, [k]: obj[k] }), {});

export const confirmPayment = orderParams => (dispatch, getState, sdk) => {
  dispatch(confirmPaymentRequest());

  const payoutInvoiceDate = new Date();
  const bodyParams = {
    id: orderParams.transactionId,
    transition: orderParams.projectTransactionId
      ? TRANSITION_CONFIRM_PROJECT
      : TRANSITION_CONFIRM_PAYMENT,
    params: {
      protectedData: {
        ...definedKeys(orderParams.protectedData),
        payoutInvoiceDate: {
          year: payoutInvoiceDate.getFullYear(),
          month: payoutInvoiceDate.getMonth() + 1,
          day: payoutInvoiceDate.getDate(),
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        },
        paymentTime: {
          year: payoutInvoiceDate.getFullYear(),
          month: payoutInvoiceDate.getMonth() + 1,
          day: payoutInvoiceDate.getDate(),
          hours: payoutInvoiceDate.getHours(),
          minutes: payoutInvoiceDate.getMinutes(),
          seconds: payoutInvoiceDate.getSeconds(),
          milliseconds: payoutInvoiceDate.getMilliseconds(),
        },
      },
    },
  };

  let order = null;

  return sdk.transactions
    .transition(bodyParams, { expand: true })
    .then(response => {
      order = response.data.data;
      dispatch(confirmPaymentSuccess(order.id));
      return order;
    })
    .then(order => {
      if (orderParams.projectTransactionId) {
        return sdk.transactions
          .transition(
            {
              id: new UUID(orderParams.projectTransactionId),
              transition: TRANSITION_ACCEPT_OFFER,
              params: {
                protectedData: {
                  newTransactionId: order.id.uuid,
                  ...definedKeys(orderParams.protectedData),
                  payoutInvoiceDate: {
                    year: payoutInvoiceDate.getFullYear(),
                    month: payoutInvoiceDate.getMonth() + 1,
                    day: payoutInvoiceDate.getDate(),
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                    milliseconds: 0,
                  },
                  paymentTime: {
                    year: payoutInvoiceDate.getFullYear(),
                    month: payoutInvoiceDate.getMonth() + 1,
                    day: payoutInvoiceDate.getDate(),
                    hours: payoutInvoiceDate.getHours(),
                    minutes: payoutInvoiceDate.getMinutes(),
                    seconds: payoutInvoiceDate.getSeconds(),
                    milliseconds: payoutInvoiceDate.getMilliseconds(),
                  },
                },
              },
            },
            {
              include: ['customer'],
              expand: true,
            }
          )
          .then(projectResponse => {
            const transactionId = projectResponse.data.data.id.uuid;
            const recipientId = projectResponse.data.data.relationships.customer.data.id.uuid;

            const { protectedData = {} } = order.attributes;
            const { lePayoutTransactionId } = protectedData;

            // Mark the project transaction as readSend the message to the created transaction
            return markTxRead(transactionId, recipientId).then(() => {
              if (lePayoutTransactionId) {
                return completePayoutTransaction(lePayoutTransactionId).then(() => {
                  return order;
                });
              }

              return order;
            });
          });
      }

      return order;
    })
    .then(() => {
      return sdk.transactions
        .show(
          { id: orderParams.transactionId, include: ['provider', 'customer', 'listing'] },
          { expand: true }
        )
        .then();
    })
    .then(response => {
      const transaction = order;
      const transactionId = transaction.id;
      const provider = response.data.data.relationships.provider.data;

      // Send the message to the created transaction
      return markTxUnread({
        transactionId: transactionId.uuid,
        recipientId: provider.id.uuid,
      }).then(() => {
        return response;
      });
    })
    .then(response => {
      if (typeof window === 'object') {
        trackIntercomEvent('Payment confirmed', {
          transactionId: response.data.data.id.uuid,
          listingId: response.data.data.relationships.listing.data.id.uuid,
          customerId: response.data.data.relationships.customer.data.id.uuid,
          providerId: response.data.data.relationships.provider.data.id.uuid,
          transition: response.data.data.attributes.lastTransition,
          listingType: orderParams.projectTransactionId ? 'project' : 'service',
          processName: response.data.data.attributes.processName,
        });
      }
      return response.data.data;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = orderParams.transactionId
        ? { transactionId: orderParams.transactionId.uuid }
        : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const cancelPayment = transactionId => (dispatch, getState, sdk) => {
  dispatch(cancelPaymentRequest());

  const bodyParams = {
    id: transactionId,
    transition: TRANSITION_CANCEL_PAYMENT,
    params: {},
  };

  return sdk.transactions
    .transition(bodyParams)
    .then(() => {
      return sdk.transactions.show({ id: transactionId });
    })
    .then(response => {
      const order = response.data.data;
      dispatch(cancelPaymentSuccess(order.id));
      return order;
    })
    .catch(e => {
      dispatch(cancelPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  let message = params.message || '';
  const bidAmount = params.bidAmount;
  const orderId = params.id;

  if (bidAmount) {
    message += ``;
  }

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (orderParams, transactionId) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;
  const transition = isTransition
    ? TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY
    : TRANSITION_REQUEST_PAYMENT;
  const isPrivilegedTransition = isPrivileged(transition);

  const bookingData = {
    startDate: orderParams.bookingStart,
    endDate: orderParams.bookingEnd,
    pricingPackage: orderParams.pricingPackage,
    suggestedPrice: orderParams.suggestedPrice,
    isCustomOffer: orderParams.isCustomOffer,
    projectBidAmount: orderParams.projectBidAmount,
    projectBidDescription: orderParams.projectBidDescription,
    projectTransactionId: orderParams.projectTransactionId,
  };

  const params = {
    ...orderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition,
        params,
      }
    : {
        processAlias: orderParams.isLightEntrepreneur
          ? config.lightProcessAlias
          : config.bookingProcessAlias,
        transition,
        params,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    const { listingId, bookingStart, bookingEnd } = params;
    log.error(e, 'speculate-transaction-failed', {
      listingId: listingId.uuid,
      bookingStart,
      bookingEnd,
    });
    return withGateCode(e).then(err => dispatch(speculateTransactionError(err)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, bookingData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, bookingData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
// The VAT outcome the billing-and-VAT panel on the checkout shows: the billing
// details used, the VIES result and the payment path. A failure only hides the
// panel; the page swallows it and the privileged initiate remains the real gate.
export const fetchVatPreview = ({ transactionId, listingId }) => dispatch => {
  dispatch({ type: FETCH_VAT_PREVIEW_REQUEST });
  const idOf = id => (id && id.uuid) || id;
  const body = transactionId
    ? { transactionId: idOf(transactionId) }
    : { listingId: idOf(listingId) };
  return vatPreviewApi(body)
    .then(result => {
      dispatch({ type: FETCH_VAT_PREVIEW_SUCCESS, payload: result });
      return result;
    })
    .catch(e => {
      dispatch({ type: FETCH_VAT_PREVIEW_ERROR, payload: storableError(e), error: true });
      throw e;
    });
};

export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};
